import { makeAutoObservable, runInAction } from 'mobx';
import { CancelError } from '@pxtra-client-lib-legacy/gen';
import { Entity } from '../../lib/base/viewModels';
import { ListStoreFetchResult } from './ListStore';

const MIN_BUFFER_SIZE = 10;
const MIN_THRESHOLD = 5;

export class EntityStreamBuffer<TEntity extends Entity<TId>, TId = TEntity['id']> {
  private data: Array<TEntity> = [];
  public head = 0;
  public streamLength?: number;
  public pendingRequestCount = 0;

  // current entities requested from the backend
  private pendingEntityCount = 0;
  // current awaited entities from the frontend
  private awaitedEntityCount = 0;
  private generation = 0;

  constructor(
    private readonly _fetch: (
      limit: number,
      offset: number
    ) => Promise<ListStoreFetchResult<TEntity>>,
    public size: number,
    public threshold = size
  ) {
    this.setThreshold(threshold);
    makeAutoObservable(this);
  }

  public setThreshold(value: number) {
    if (value > this.size) {
      this.threshold = this.size;
      return;
    }
    this.threshold = Math.max(MIN_THRESHOLD, value);
  }

  public changeSize(size: number, threshold = size) {
    this.size = Math.max(MIN_BUFFER_SIZE, size);
    this.setThreshold(threshold);
  }

  private lastRequest: Promise<void> = new Promise((resolve) => resolve());

  public get isLoading() {
    return this.pendingRequestCount > 0;
  }

  private isBelowThreshold(bufferLength: number) {
    return bufferLength < this.threshold;
  }

  private isFirstConsume() {
    return this.data.length == 0 && this.pendingRequestCount == 0;
  }

  private isStale(generation: number) {
    return generation !== this.generation;
  }

  public async consume(entityCount: number) {
    const generation = this.generation;

    if (this.isFirstConsume()) {
      await this.fetch(this.size);
      if (this.isStale(generation)) return [];
    }

    if (!(await this.fill(entityCount, generation))) return [];

    return this.data.splice(0, entityCount);
  }

  /**
   * Makes sure the buffer holds what consuming `entityCount` is about to take,
   * fetching more entities when it runs below the threshold. Returns false when
   * a clear() crossed one of the awaits, meaning the caller consumes on behalf
   * of a query that is no longer displayed.
   */
  private async fill(entityCount: number, generation: number) {
    // describes how many entities remain after consumption
    const newBufferLength = this.data.length - entityCount;

    if (!this.isBelowThreshold(newBufferLength + this.pendingEntityCount)) return true;

    /* overflow occurs when more entities are requested
     * than the size of the buffer */
    const overflow = newBufferLength < 0 ? Math.abs(newBufferLength) : 0;

    if (overflow === 0) {
      // Read ahead for the next consumer, who is the one that will await it.
      this.fetch(this.size - newBufferLength);
      return true;
    }

    // Requests already in flight cover the overflow, so waiting for them suffices.
    if (this.pendingEntityCount > overflow) {
      const newAwaitedEntities = overflow - this.awaitedEntityCount;
      this.awaitedEntityCount += newAwaitedEntities;
      await this.lastRequest;
      if (this.isStale(generation)) return false;
      this.awaitedEntityCount -= newAwaitedEntities;
      return true;
    }

    await this.fetch(this.size + overflow - this.awaitedEntityCount);
    return !this.isStale(generation);
  }

  /**
   * A failed request is logged and otherwise dropped: nothing is pushed into the
   * buffer and `streamLength` stays unset, so the buffer keeps a consistent state
   * and the next read retries the fetch. The caller sees an empty page, not an
   * exception — the chain must survive a single failure, otherwise every later
   * request queued behind it would be rejected too.
   */
  private async manageRequest(request: Promise<ListStoreFetchResult<TEntity>>, generation: number) {
    this.lastRequest = this.lastRequest
      .then(async () => {
        await this.processRequest(request, generation);
      })
      .catch((error_) => {
        if (error_ instanceof CancelError) return;
        console.error(error_);
      });
    return this.lastRequest;
  }

  public get isEndOfStream() {
    return this.streamLength ? this.head > this.streamLength : false;
  }

  private async fetch(entityCount: number) {
    if (this.isEndOfStream) return;
    const generation = this.generation;
    const request = this._fetch(entityCount, this.head).finally(() => {
      if (this.isStale(generation)) return;
      runInAction(() => {
        this.pendingRequestCount--;
        this.pendingEntityCount -= entityCount;
      });
    });
    runInAction(() => {
      this.pendingRequestCount++;
      this.pendingEntityCount += entityCount;
      this.head += entityCount;
    });
    return this.manageRequest(request, generation);
  }

  private async processRequest(
    request: Promise<ListStoreFetchResult<TEntity>>,
    generation: number
  ) {
    const response = await request;
    if (this.isStale(generation)) return;
    runInAction(() => {
      this.data.push(...response.entities);
      this.streamLength ??= response.totalCount;
    });
  }

  public clear() {
    runInAction(() => {
      this.generation++;
      this.data = [];
      this.head = 0;
      this.streamLength = undefined;
      this.pendingRequestCount = 0;
      this.pendingEntityCount = 0;
      this.awaitedEntityCount = 0;
      this.lastRequest = Promise.resolve();
    });
  }
}
